import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ConsultationAttachmentFile, ConsultationSectionDataMap, ConsultationSectionKey,
} from '../models/clinical-history.model';

// The same eleven consultation slices as `IPatientsRepository.getConsultationSection`, reached
// through the `ClientsRead`-gated mirror route, for when the detail page was opened from a client
// record (`?from=client:{id}`).
//
// It is declared here rather than in `features/web/clients` because the dependency may only run one
// way: `clients` is allowed to import `patients` (Rule 4's cross-cutting whitelist in
// `tools/check-architecture.mjs`), never the reverse. So `patients` owns the port and `clients`
// binds the implementation in its own `provideClientsData()`.
export interface IClientConsultationSectionsGateway {
  getConsultationSection<K extends ConsultationSectionKey>(
    clientId: string, patientId: string, consultationId: string, section: K,
  ): Observable<ConsultationSectionDataMap[K]>;
  // ProVet has no PDF endpoint for written prescriptions, so the backend renders one on demand --
  // and only under `/api/clients/...`. There is no patients-scoped mirror, which is why the
  // prescriptions section offers the download exclusively when this port is in play.
  downloadPrescriptionPdf(
    clientId: string, patientId: string, prescriptionExternalId: string, locale: string,
  ): Observable<Blob>;
  downloadConsultationAttachment(
    clientId: string, patientId: string, consultationId: string, noteId: string, uploadId: string,
  ): Observable<Blob>;
  // `ClientsRead`-gated mirror of `IPatientsRepository.getConsultationAnalysisReferralAttachments`.
  getConsultationAnalysisReferralAttachments(
    clientId: string, patientId: string, consultationId: string, referralId: string,
  ): Observable<readonly ConsultationAttachmentFile[]>;
}

export const CLIENT_CONSULTATION_SECTIONS_GATEWAY =
  new InjectionToken<IClientConsultationSectionsGateway>('IClientConsultationSectionsGateway');
